/**
 * What is left of the bundled catalogue: the chrome B5, B6 and B7 draw, and the table
 * that turns a domain's glyph KEY into a drawn mark.
 *
 * ── WHAT USED TO BE IN THIS FILE ────────────────────────────────────────────
 * The whole catalogue. Twelve life domains with their names and subtitles, three
 * intent groups, ten categories, three providers and their visit fees — shipped inside
 * the JavaScript bundle to every phone. A visit fee could not change, a provider could
 * not be suspended, a category could not close and a sixth domain could not go live
 * without a deploy of the web app, and until one happened the app went on telling every
 * customer that নূর রেফ্রিজারেশন charges ৳ 350 to come out.
 *
 * It now comes from `./api.ts` — docs/05 § offerings' three routes. What stays here is
 * the part that is the SAME for every person and is drawn rather than stored:
 *
 *   · `EXPLORE_COPY`  — B5's title, its search placeholder and the আসছে band
 *   · `CATEGORY_COPY` — B7's ভিজিট ফি, সাজান and ফিল্টার labels
 *   · `MARK_FOR`      — glyph key → drawn mark, the design-asset table
 *
 * SOURCES — nothing here is invented.
 *   · Names, codes and launch phase: `Oprappo Architecture.dc.html § 04`, the twelve-row
 *     "জীবন ক্ষেত্র · Life domain" table. Five are P0 and seven are P1/P2, which is
 *     exactly B5's "পাঁচটি ক্ষেত্র এখন চালু · সাতটি আসছে".
 *   · B5's labels and the five live rows' glyphs: `Oprappo P0 Screens.dc.html`, frame B5.
 *   · Glyphs for four of the unlaunched rows: `Oprappo P2 Projects and Explore.dc.html`,
 *     frame EX1, the only frame that draws all of them.
 *
 * REPORTED GAPS
 *   · Domain 10, ডিজিটাল ও যোগাযোগ, has no glyph in ANY design file. EX1 draws eleven
 *     domains in three groups while its own caption claims "four domains each" — the
 *     missing twelfth is domain 10. Its row renders with the icon slot held open and no
 *     mark, because inventing a glyph is not a decision this package gets to make. A row
 *     whose `glyph` is null, or carries a key this table does not have, renders the same
 *     way for the same reason.
 *   · B5's dimmed rows carry no chevron and no destination. They are not links here.
 */
import type { IconName } from '@oprappo/ui';
import type { GlyphName } from '../onboarding/glyphs.js';
import { isDomainGlyphKey, type DomainGlyphKey } from './api.js';

export type DomainMark =
  | { from: 'icon'; name: IconName }
  | { from: 'glyph'; name: GlyphName }
  /** No glyph exists in any design file, or the server named one this table has not. */
  | { from: 'none' };

/**
 * Glyph key → drawn mark.
 *
 * The key set is closed (`./api.ts § DOMAIN_GLYPHS`) and this is the only place it meets
 * a drawing. A server that names a key outside the set gets `{ from: 'none' }` — the slot
 * held open, the names still aligned, nothing drawn in it — rather than a thrown render
 * or a substituted mark that would quietly say the wrong thing about a domain.
 */
const MARK_FOR: Record<DomainGlyphKey, DomainMark> = {
  bill: { from: 'icon', name: 'bill' },
  food: { from: 'glyph', name: 'food' },
  market: { from: 'icon', name: 'market' },
  transport: { from: 'icon', name: 'transport' },
  serviceDomain: { from: 'glyph', name: 'serviceDomain' },
  home: { from: 'icon', name: 'home' },
  pin: { from: 'glyph', name: 'pin' },
  education: { from: 'glyph', name: 'education' },
  work: { from: 'glyph', name: 'work' },
  family: { from: 'glyph', name: 'family' },
  government: { from: 'glyph', name: 'government' },
};

export function markFor(glyph: string | null): DomainMark {
  if (glyph === null || !isDomainGlyphKey(glyph)) return { from: 'none' };
  return MARK_FOR[glyph];
}

/** B5's header and its tinted divider, exactly as drawn. */
export const EXPLORE_COPY = {
  title_bn: 'এক্সপ্লোর',
  search_placeholder_bn: 'সেবা খুঁজুন',
  coming_band_bn: 'আসছে',
} as const;

/** Fixed labels drawn in B7. */
export const CATEGORY_COPY = {
  visit_fee_bn: 'ভিজিট ফি',
  sort_bn: 'সাজান',
  filter_bn: 'ফিল্টার',
} as const;
